import { call, fork, put, select, takeLatest } from 'redux-saga/effects';
import { getCurrentTenant18n } from 'i18n';
import api from 'api';
import { captureError, setUserContext } from 'sentry';

import { push } from 'connected-react-router';

import appConfig from 'config';

import reducersUtils from 'state/utils';
import { actions as authActions, selectors as authSelectors } from 'state/auth';
import { actions as appActions } from 'state/app';
import {
  actions as settingsActions,
  selectors as settingsSelectors,
} from 'state/settings';
import { actions as clientConfigActions } from 'state/clientConfig';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';

import * as addressEffects from 'state/addresses/effects';
import * as statusEffects from 'state/status/effects';

import { identify as inspectletIdentify } from 'analytics/inspectlet';
import { identify as segmentIdentify } from 'analytics/segment';
import { identify as beamerIdentify } from 'analytics/beamer';

import { isEmpty } from 'utils/types';
import routes from 'utils/routes';

import types from 'state/app/actionTypes';

import Refiner from 'refiner-js';
import { IS_TENANT } from 'tenant/utils';

function* initCredentials() {
  const credentials = yield select(authSelectors.getCredentials);

  if (!isEmpty(credentials)) {
    yield call(api.setCredentials, credentials);
    yield put(authActions.setCredentials(credentials));
  }
}

function* initUserShipperAndCountrySettings() {
  const { data: me } = yield call(api.fetchMe);
  const {
    country_settings: countrySettings,
    feature_flags: featureFlags,
    client_config: clientConfig,
    ...rest
  } = me;
  const { data: shipper } = yield call(api.authGet, me.shipper);

  if (me.is_staff) {
    yield put(authActions.logout());
    return;
  }

  yield put(authActions.setUser({ ...rest }));
  yield put(authActions.setShipper({ shipper }));
  yield put(settingsActions.setCountrySettings(countrySettings));
  yield put(settingsActions.setFeatureFlags(featureFlags));
  yield put(clientConfigActions.setClientConfig(clientConfig));
}

function* initi18n() {
  const locale = yield select(settingsSelectors.getLocale);
  const i18n = getCurrentTenant18n();
  yield call(i18n.changeLanguage.bind(i18n), locale);
}

function* initInspectlet() {
  const user = yield select(authSelectors.getUser);
  try {
    yield call(inspectletIdentify, { user });
  } catch (err) {
    captureError(err);
  }
}

function* initSegment() {
  const user = yield select(authSelectors.getUser);
  try {
    yield call(segmentIdentify, user);
  } catch (err) {
    captureError(err);
  }
}

function* setSentryUserContext() {
  const user = yield select(authSelectors.getUser);
  const permissions = yield select(authSelectors.getUserPermissions) || [];

  const forceClassicForm = permissions.includes('core.force_classic_form');
  const showNewForm = !forceClassicForm;
  const view_product_catalog = showNewForm;

  try {
    yield call(setUserContext, {
      user_id: user.id,
      email: user.email,
      shipper_name: user.company_name,
      shipper_id: user.shipper_id,
      view_product_catalog,
    });
  } catch (err) {
    console.error('[setSentryUserContext]:', err);
  }
}

function* initBeamer() {
  const user = yield select(authSelectors.getUser);
  const language = yield select(settingsSelectors.getLocale);
  try {
    if (user) {
      yield call(beamerIdentify, { user, language });
    }
  } catch (err) {
    captureError(err);
  }
}

function* initRefiner() {
  const me = yield select(authSelectors.getUser);
  const language = yield select(settingsSelectors.getLocale);
  if (IS_TENANT) return;
  try {
    yield call(Refiner, 'setProject', appConfig.REFINER.PROJECT_ID);
    yield call(Refiner, 'identifyUser', {
      type: 'SH',
      id: me.id.toString(),
      name: me.name,
      country: me.country,
      language,
      created_at: me.created_at,
      email: me.email,
      account: {
        id: me.shipper_id.toString(),
        name: me.company_name,
        created_at: me.shipper_created_at,
      },
    });
  } catch (e) {
    captureError(e);
  }
}

function* handleResetApp() {
  yield put(authActions.setCredentials(null));
  yield put(authActions.setUser(null));
  yield put(authActions.setShipper({ shipper: null }));
  yield put(settingsActions.setCountrySettings({}));
  yield put({ type: reducersUtils.RESET_ORDER_STATE });
  yield put({ type: reducersUtils.RESET_ORDERS_STATE });
  yield call(() => localStorage.clear());
  yield put(push(routes.login));
}

export function* sendUtmIfNeeded() {
  try {
    let utmData = false;
    if (
      window &&
      window.localStorage &&
      window.localStorage.getItem('utmData')
    ) {
      utmData = JSON.parse(window.localStorage.getItem('utmData'));
    }
    if (utmData) {
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.UtmCampaignEvent({
            eventData: { ...utmData },
          })
        )
      );

      yield call(window.localStorage.removeItem, 'utmData');
    }
  } catch (err) {
    captureError(err);
  }
}

export function* handleInitApp() {
  yield put(authActions.setLoading(true));

  try {
    yield call(initCredentials);
    yield call(initUserShipperAndCountrySettings);
    yield call(initRefiner);
    yield call(setSentryUserContext);
    yield call(initi18n);
    yield call(initSegment);
    yield call(initInspectlet);
    yield call(initBeamer);
    yield call(addressEffects.initAddresses);
    yield fork(statusEffects.initStatusCheck);
    yield fork(sendUtmIfNeeded);
  } catch (err) {
    captureError(err);
    yield call(handleResetApp);
  }
  yield put(authActions.setLoading(false));
  yield put(appActions.initAppFinished());
}

export default function* () {
  yield takeLatest(types.initApp$, handleInitApp);
  yield takeLatest(types.resetApp$, handleResetApp);
  yield takeLatest(types.updateUser$, initUserShipperAndCountrySettings);
}

export {
  initRefiner,
  initBeamer,
  initUserShipperAndCountrySettings,
  setSentryUserContext,
};
